<script setup>
  import { ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { debounce } from '../../utils/debounce.js';

  const { t } = useI18n();
  const i18nScope = ref('components.imprints_list');

  const props = defineProps({
    searchQuery: {
      type: String,
      default: ''
    },
    selectedAudience: {
      type: String,
      default: ''
    },
    selectedTerritory: {
      type: String,
      default: ''
    },
    selectedLanguage: {
      type: String,
      default: ''
    },
    showOnlyFollowed: {
      type: Boolean,
      default: false
    },
    showFollowedFilter: {
      type: Boolean,
      default: true
    },
    audiences: {
      type: Array,
      required: true
    },
    territories: {
      type: Array,
      default: () => []
    },
    languages: {
      type: Array,
      default: () => []
    },
    countriesOfPublication: {
      type: Array,
      default: () => []
    },
    selectedCountryOfPublication: {
      type: String,
      default: ''
    }
  });

  const emit = defineEmits([
    'update:searchQuery',
    'update:selectedAudience',
    'update:selectedTerritory',
    'update:selectedLanguage',
    'update:showOnlyFollowed',
    'filter-change',
    'search',
    'audience-change',
    'territory-change',
    'language-change',
    'country-of-publication-change',
    'followed-change'
  ]);

  const localSearchQuery = ref(props.searchQuery);
  const localSelectedAudience = ref(props.selectedAudience);
  const localSelectedTerritory = ref(props.selectedTerritory);
  const localSelectedLanguage = ref(props.selectedLanguage);
  const localSelectedCountryOfPublication = ref(props.selectedCountryOfPublication);
  const localShowOnlyFollowed = ref(props.showOnlyFollowed);

  const handleSearchInput = async () => {
    emit('update:searchQuery', localSearchQuery.value);
    const shouldSearch = await debounce(localSearchQuery.value, 1200, 3);
    if (shouldSearch) {
      emit('search', localSearchQuery.value);
    }
  };

  const handleClearSearch = () => {
    localSearchQuery.value = '';
    emit('update:searchQuery', '');
    emit('search', '');
  };

  const handleAudienceChange = () => {
    emit('update:selectedAudience', localSelectedAudience.value);
    emit('audience-change', localSelectedAudience.value);
  };

  const handleTerritoryChange = () => {
    emit('update:selectedTerritory', localSelectedTerritory.value);
    emit('territory-change', localSelectedTerritory.value);
  };

  const handleLanguageChange = () => {
    emit('update:selectedLanguage', localSelectedLanguage.value);
    emit('language-change', localSelectedLanguage.value);
  };

  const handleCountryOfPublicationChange = () => {
    emit('update:selectedCountryOfPublication', localSelectedCountryOfPublication.value);
    emit('country-of-publication-change', localSelectedCountryOfPublication.value);
  };

  const handleFollowedChange = () => {
    emit('update:showOnlyFollowed', localShowOnlyFollowed.value);
    emit('followed-change', localShowOnlyFollowed.value);
  };
</script>

<template>
  <div class="imprints-filters">
    <div class="row g-3">
      <!-- Search Input -->
      <div class="col-md-3">
        <div class="input-group">
          <input
            v-model="localSearchQuery"
            type="text"
            class="form-control"
            :placeholder="t(`${i18nScope}.filters.search_placeholder`)"
            @input="handleSearchInput"
          />
          <button
            v-if="localSearchQuery"
            class="btn btn-outline-secondary"
            type="button"
            @click="handleClearSearch"
          >
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
      </div>

      <div class="col-md-3">
        <!-- Select for languages -->
        <select
          class="form-select"
          v-model="localSelectedLanguage"
          @change="handleLanguageChange"
        >
          <option value="">{{ t(`${i18nScope}.filters.all_content_languages`) }}</option>
          <option
            v-for="language in languages"
            :key="language.id"
            :value="language.id"
          >
            {{ language.name }}
          </option>
        </select>
      </div>

      <div class="col-md-3">
        <!-- Select for audiences -->
        <select
          class="form-select"
          v-model="localSelectedAudience"
          @change="handleAudienceChange"
        >
          <option value="">{{ t(`${i18nScope}.filters.all_audiences`) }}</option>
          <option
            v-for="audience in audiences"
            :key="audience.id"
            :value="audience.id"
          >
            {{ audience.name }}
          </option>
        </select>
      </div>
      <div class="col-md-3">
        <!-- Select for territories -->
        <select
          class="form-select"
          v-model="localSelectedTerritory"
          @change="handleTerritoryChange"
        >
          <option value="">{{ t(`${i18nScope}.filters.all_territories`) }}</option>
          <option
            v-for="(territory) in territories"
            :key="territory.id"
            :value="territory.id"
          >
            {{ territory.fullname }}
          </option>
        </select>
      </div>
    </div>

    <div v-if="countriesOfPublication.length" class="row g-3 mt-2">
      <div class="col-md-3">
        <select
          class="form-select"
          v-model="localSelectedCountryOfPublication"
          @change="handleCountryOfPublicationChange"
        >
          <option value="">{{ t(`${i18nScope}.filters.all_countries_of_publication`) }}</option>
          <option
            v-for="country in countriesOfPublication"
            :key="country.id"
            :value="country.id"
          >
            {{ country.name }}
          </option>
        </select>
      </div>
    </div>

    <div v-if="showFollowedFilter" class="row g-3 my-2">
      <div class="col-md-3">
        <div class="form-check form-switch">
          <input 
            class="form-check-input" 
            type="checkbox" 
            id="flexSwitchCheckDefault"
            v-model="localShowOnlyFollowed"
            @change="handleFollowedChange"
            role="button"
          >
          <label class="form-check-label" for="flexSwitchCheckDefault">
            {{ t(`${i18nScope}.filters.show_only_followed`) }}
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .imprints-filters {
    margin-bottom: 20px;
  }

  .input-group {
    .btn-outline-secondary {
      border-left: 0;
    }
  }
</style>
